import { useEffect, useState, type ReactElement } from 'react';
import type { PostDto } from '@gr/core/content/posts/dto';
import { useNavigate, useSearchParams, useServices, useT } from '../../../app/di';
import { moveItem } from '../../../shared/collections';
import { useAction, useResource } from '../../../shared/hooks';
import { Alert } from '../../../ui-kit/Alert/Alert';
import { Button } from '../../../ui-kit/Button/Button';
import { Card } from '../../../ui-kit/Card/Card';
import { MediaPickerDialog } from '../media';
import { useBlog } from '../../accounts/workspaces';
import { withPublish } from './model/post-media-selection';
import { VIEWER_QUERY, viewerPosition } from './model/post-media-viewer';
import { PostMediaStrip } from './PostMediaStrip/PostMediaStrip';
import { PostMediaViewerConnected } from './PostMediaViewerConnected';

// POST-UC-3, POST-UC-3.CC-1, POST-UC-13, POST-UC-13.CC-1, POST-UC-14
export function PostMediaPanel({ post, editable, onChanged }: { post: PostDto; editable: boolean; onChanged(post: PostDto): void }): ReactElement {
  const { api } = useServices();
  const { t, te } = useT();
  const { blog } = useBlog();
  const [picking, setPicking] = useState(false);
  const media = useResource(() => api.media.list(blog.id), [api, blog.id, post.mediaIds.join(',')]);
  const setMedia = useAction((ids: string[], excluded?: string[]) => api.posts.setMedia(blog.id, post.id, ids, excluded));
  const navigate = useNavigate();
  const viewing = useSearchParams().get(VIEWER_QUERY);
  const position = viewing ? viewerPosition(post.mediaIds, viewing) : null;

  // POST-UC-13.CC-1: медиа из ссылки больше не в посте — просмотр не открывается, адрес очищается.
  useEffect(() => {
    if (viewing && !position) navigate('post', { blogId: blog.id, postId: post.id }, { replace: true, keepScroll: true });
  }, [viewing, position, navigate, blog.id, post.id]);

  // POST-UC-14: отметка видна сразу, до ответа сервера; при ошибке возвращается прежняя.
  const [pendingExcluded, setPendingExcluded] = useState<string[] | null>(null);
  const excluded = new Set(pendingExcluded ?? post.excludedMediaIds);

  const apply = async (ids: string[], excludedIds?: string[]): Promise<void> => {
    const result = await setMedia.run(ids, excludedIds);
    if (result.success) onChanged(result.data);
  };

  const togglePublish = async (mediaId: string, publish: boolean): Promise<void> => {
    const next = withPublish({ mediaIds: post.mediaIds, excludedMediaIds: [...excluded] }, mediaId, publish);
    setPendingExcluded(next);
    await apply(post.mediaIds, next);
    setPendingExcluded(null);
  };

  return (
    <Card
      title={t('posts.media.title')}
      actions={
        editable ? (
          <Button size="sm" icon="plus" onClick={() => setPicking(true)}>
            {t('posts.media.add')}
          </Button>
        ) : null
      }
    >
      <div className="u-stack">
        <p className="u-muted u-small">{t('posts.media.hint')}</p>
        {setMedia.error ? <Alert tone="danger">{te(setMedia.error)}</Alert> : null}
        <PostMediaStrip
          blogId={blog.id}
          postId={post.id}
          items={post.mediaIds.map((mediaId) => ({ mediaId, media: media.data?.find((m) => m.id === mediaId), publish: !excluded.has(mediaId) }))}
          editable={editable}
          pending={setMedia.pending}
          onMove={(index, delta) => void apply(moveItem(post.mediaIds, index, delta))}
          onRemove={(mediaId) => void apply(post.mediaIds.filter((id) => id !== mediaId))}
          onTogglePublish={(mediaId, publish) => void togglePublish(mediaId, publish)}
        />
      </div>
      {viewing && position ? <PostMediaViewerConnected post={post} mediaId={viewing} position={position} /> : null}
      {picking ? (
        <MediaPickerDialog
          blogId={blog.id}
          alreadyAttached={post.mediaIds}
          onClose={() => setPicking(false)}
          onPick={async (ids) => {
            setPicking(false);
            await apply([...post.mediaIds, ...ids]);
          }}
        />
      ) : null}
    </Card>
  );
}
